import moment from 'moment';
import { BadgeId } from './containers/Badges/Models';
import { useIntl } from 'react-intl';

interface AnalysisFeature<T> {
  readonly status: AnalysisStatus;
  readonly analysis: T;
}

export enum AnalysisStatus {
  notAnalyzed = 0,
  analyzed = 1,
  notEnoughText = 2,
  unsupportedLanguage = 3,
  unexpectedError = 4,
  networkError = 5,
  analyzing = 6,
}

export const isAnalysisFailed = (analysis?: AnalysisStatus): boolean => {
  if (analysis) {
    return (
      analysis === AnalysisStatus.notEnoughText ||
      analysis === AnalysisStatus.unsupportedLanguage ||
      analysis === AnalysisStatus.unexpectedError ||
      analysis === AnalysisStatus.networkError
    );
  } else {
    return false;
  }
};

export const isAnalysisAnalyzed = (analysis?: AnalysisStatus): boolean => {
  if (analysis) {
    return analysis === AnalysisStatus.analyzed || analysis === AnalysisStatus.analyzing;
  } else {
    return false;
  }
};

export const analysisStatusDescriptionMessage = (analysis: AnalysisStatus): string => {
  const intl = useIntl();
  const messageBase = 'containers.Insights.EmotionsAndTopics.status';

  switch (analysis) {
    case AnalysisStatus.notAnalyzed:
      return intl.formatMessage({
        id: `${messageBase}.notAnalyzed`,
      });
    case AnalysisStatus.notEnoughText:
      return intl.formatMessage({
        id: `${messageBase}.notEnoughText`,
      });
    case AnalysisStatus.unsupportedLanguage:
      return intl.formatMessage({
        id: `${messageBase}.unsupportedLanguage`,
      });
    case AnalysisStatus.unexpectedError:
      return intl.formatMessage({
        id: `${messageBase}.unexpectedError`,
      });
    case AnalysisStatus.analyzing:
      return intl.formatMessage({
        id: `${messageBase}.analyzing`,
      });
    case AnalysisStatus.networkError:
      return intl.formatMessage({
        id: `${messageBase}.networkError`,
      });
    default:
      return ``;
  }
};

export enum Emotion {
  joy = 'joy',
  fear = 'fear',
  disgust = 'disgust',
  sadness = 'sadness',
  anger = 'anger',

  // TODO: Remove in the future
  sandess = 'sandess',

  surprise = 'surprise',
  love = 'love',
  excitement = 'excitement',
  confusion = 'confusion',
  shame = 'shame',
}

export interface EmotionAnalysis {
  readonly relevance: number;
  readonly category: Emotion;
  readonly subcategory: string | null;
  readonly context: string | null;
  readonly mixedEmotion: Emotion | null;
}

export interface EmotionAnalysisFeature extends AnalysisFeature<ReadonlyArray<EmotionAnalysis>> {}

export interface KeywordAnalysis {
  readonly relevance: number;
  readonly name: string;
}

export interface KeywordAnalysisFeature extends AnalysisFeature<ReadonlyArray<KeywordAnalysis>> {}

export interface SentimentAnalysisFeature extends AnalysisFeature<number> {}

export interface PageAnalysis {
  readonly date: string;
  readonly emotions: EmotionAnalysisFeature;
  readonly concepts: KeywordAnalysisFeature;
  readonly sentiment: SentimentAnalysisFeature;
}

export type PageId = string;

export const isPageAnalyzed = (page: Page) => {
  if (!page.analysis) {
    return false;
  }

  const features = [page.analysis.concepts, page.analysis.emotions, page.analysis.sentiment];

  const analyzedFeatures = features.find(feature => {
    return feature.status === AnalysisStatus.analyzed;
  });

  if (analyzedFeatures) {
    return true;
  }

  return false;
};

export interface WritingInterval {
  start: Date;
  finish: Date;
}

export interface Page {
  [x: string]: any;
  userId: number;
  dateId: PageId;
  text: string;
  createdAt: string;
  updatedAt: string;
  startedWritingAt: string;
  finishedWritingAt: string;
  isDeleted: boolean;
  lastSyncDate?: string;
  wordsGoal: number;
  analysis?: PageAnalysis;
  writingIntervals: WritingInterval[];
}

export const wordsCount = (page: Page) => {
  return page.text.split(/\s+/).filter(n => n !== '').length;
};

export interface PagesMap {
  [key: string]: Page;
}

export interface UserSettings {
  readonly wordsGoal: number;
}

export enum SubscriptionType {
  none = 'none',
  apple = 'apple',
  gumroad = 'gumroad',
}

export const TRIAL_PAGES_NUMBER = 5;

export interface SubscriptionState {
  isSubscribed: boolean;
  expiresDate: string;
  isCancelled: boolean;
  type: SubscriptionType;
  isEligibleForIntroductoryPrice: boolean;
  trialAnalysisLeft: number;
}

export const isProFeaturesAvailable = (subscriptionState: SubscriptionState | null): boolean => {
  if (subscriptionState === null) {
    return false;
  } else {
    return subscriptionState.isSubscribed || subscriptionState.trialAnalysisLeft > 0;
  }
};

export interface UserSession {
  readonly id: number;
  readonly email: string;
  readonly type: string;
  readonly token: string;
  readonly subscription: SubscriptionState;
  readonly settings: UserSettings;
  readonly badges: ReadonlyArray<BadgeId>;
}

export const getStartedWritingAt = (writingIntervals: WritingInterval[]): Date | null => {
  if (writingIntervals.length > 0) {
    return writingIntervals[0].start;
  } else {
    return null;
  }
};

export const getFinishedWritingAt = (writingIntervals: WritingInterval[]): Date | null => {
  if (writingIntervals.length > 0) {
    const secondsWriting = writingIntervals
      .map(interval => {
        return moment(interval.finish).diff(interval.start, 'seconds');
      })
      .reduce((sum, current) => sum + current, 0);

    const firstInterval = writingIntervals[0];

    return moment(firstInterval.start)
      .add(secondsWriting, 'seconds')
      .toDate();
  } else {
    return null;
  }
};

// Seconds
export const getPageTotalWritingTimeInSeconds = (page: Page): number => {
  return moment(page.finishedWritingAt).diff(page.startedWritingAt, 'second');
};



// WEBPACK FOOTER //
// ./src/Models.ts